import React, { useState, useEffect } from 'react';
import { CTA, Richtext } from '@volkswagen-onehub/components-core';
import {
  StyledTosWrapper,
  StyledSubmitButtonContainer,
  StyledDeclineButtonContainer,
} from '../styles/tos-styles';
import { onVWBasicFormStepButtonClickEvent, onVWBasicFormStepLoadEvent, onVWBasicLinkClickEvent } from '../../../shared/tagging/tagging-helpers';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import mcEnCA from '../../../legal/mc-en-ca';
import mcFrCA from '../../../legal/mc-fr-ca';

interface MarketingConsentProps {
  onComplete: () => void;
}

const MARKETING_CONSENT_TOS_TYPE = 'MARKETING_COMMUNICATIONS_CONSENT';

export const MarketingConsent = ({ onComplete }: MarketingConsentProps) => {
  const { authService, translationHandler } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();
  const isFrench = localeService?.language === 'fr';
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const trackingManager = useTrackingManager();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    onVWBasicFormStepLoadEvent(trackingManager, {});
  }, []);

  const handleConsent = async (accepted: boolean) => {
    setIsSubmitting(true);
    const tosStatus = accepted ? 'Accept' : 'Decline';
    try {
      await authService.saveUserMyVWTosResponse(localeCode, MARKETING_CONSENT_TOS_TYPE, tosStatus);
    } catch (error) {
      console.error('Marketing consent save failed:', error);
      authService.apiLog?.({ code: 'MARKETING_CONSENT_SAVE_ERROR', message: error });
    } finally {
      setIsSubmitting(false);
    }

    onVWBasicFormStepButtonClickEvent(trackingManager, {
      Type: 'Button',
      Name: translationHandler(
        accepted
          ? 'registration.marketingconsent.accept'
          : 'registration.marketingconsent.decline',
      ),
    });

    if (accepted) {
      onVWBasicLinkClickEvent(
        trackingManager,
        { Name: translationHandler('registration.marketingconsent.accept') },
        null,
      );
    }

    onComplete();
  };

  return (
    <StyledTosWrapper>
      <Richtext
        markdown={isFrench ? mcFrCA : mcEnCA}
        dangerouslyParseRawHTML
      />
      <StyledSubmitButtonContainer>
        <CTA
          aria-label={translationHandler('registration.marketingconsent.accept')}
          emphasis="primary"
          stretchContent
          tag="button"
          type="button"
          disabled={isSubmitting}
          onClick={() => handleConsent(true)}
        >
          {translationHandler('registration.marketingconsent.accept')}
        </CTA>
      </StyledSubmitButtonContainer>
      <StyledDeclineButtonContainer>
        <CTA
          aria-label={translationHandler('registration.marketingconsent.decline')}
          emphasis="tertiary"
          stretchContent
          tag="button"
          type="button"
          disabled={isSubmitting}
          onClick={() => handleConsent(false)}
        >
          {translationHandler('registration.marketingconsent.decline')}
        </CTA>
      </StyledDeclineButtonContainer>
    </StyledTosWrapper>
  );
};
